import { Button, ScrollView, Toolbar, Tooltip } from 'devextreme-react';
import SelectBox, { Button as SelectBoxButton } from 'devextreme-react/select-box';
import Form, { GroupItem, Label, SimpleItem } from 'devextreme-react/form';
import { useCallback, useEffect, useRef, useState } from 'react';
import Popup, { ToolbarItem } from 'devextreme-react/popup';
import { useAppDispatch, useAppSelector } from '../../../../../contexts/hooks';
import { showModalTemplates } from '../../../../../contexts/slices/orders';
import { useScreenSize } from '../../../../../utils/media-query';
import { FocusInEvent } from 'devextreme/ui/select_box';
import { newToast } from '../../../..';
import { RequiredRule } from 'devextreme-react/data-grid';
import 'devextreme-react/text-area';
import { Item } from 'devextreme-react/toolbar';
import { resButtonsClear, resButtonsPin, resGridStatus1, resGridStatus2, resGridStatus3, resGridStatus4, resGridStatus5 } from '../../../../../images/icons';
import { ResultEstadoResult } from '../../../../../types/types.orders';
import { UseApiResults } from '../../../../../api/api.results';

interface FormData {
  Group: string;
  KeyValue: string;
  Type: string;
  Label: string;
  Items: string[];
  ColSpan: number;
}

const dataFrm = {};

export const ModalResultTemplates = () => {
  const dataModal = useAppSelector((x) => x.globalOrders.CurrentOrder.ModalResultTemplate);
  const Prm_Conf_Campos_Diseno = useAppSelector((x) => x.userInfo.Permissions?.Prm_Conf_Campos_Diseno) || false;
  const dispatch = useAppDispatch();
  const frmRef = useRef<Form>(null);
  const frmRefTemplate = useRef<Form>(null);
  const { isMedium, isXSmall, isSmall } = useScreenSize();
  const [modalPlantillaVisible, setModalPlantillaVisible] = useState<boolean>(false); //modal de guardar plantillas predeterminadas
  const [saveTemplate, setSaveTemplate] = useState<{}>({});
  const { putResultTemplate } = UseApiResults();
  const [data, setData] = useState<FormData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [groups, setGroups] = useState<{ [key: string]: FormData[] }>({});

  useEffect(() => {
    if (!dataModal?.Visible) {
      frmRef.current?.instance.resetValues();
      setData([]);
      setGroups({});
      setIsChanged(false);
      //hacer que dataFrm sea un objeto vacio sin propiedades
      Object.keys(dataFrm).forEach((key) => {
        delete dataFrm[key];
      });
      return;
    }

    if (dataModal?.An_Plantilla) {
      const parsedData = JSON.parse(dataModal.An_Plantilla);
      setData(parsedData);
      const newGroups: { [key: string]: FormData[] } = {};
      parsedData.forEach((item: FormData) => {
        if (!newGroups[item.Group]) {
          newGroups[item.Group] = [];
        }
        newGroups[item.Group].push(item);
      });
      setGroups(newGroups);

      if (dataModal.Res_Resultado) {
        const dataResult = JSON.parse(dataModal.Res_Resultado);

        // Convertir del formato de resultado a formato de formulario con claves únicas
        const formDataForForm = {};

        if (Array.isArray(dataResult)) {
          // Formato nuevo: array de grupos con items
          dataResult.forEach((group) => {
            if (group.Items && Array.isArray(group.Items)) {
              group.Items.forEach((item) => {
                // Crear la clave única correspondiente al formulario
                const uniqueKey = `${group.Group}|${item.Key}`;
                formDataForForm[uniqueKey] = item.Value;
              });
            }
          });
        } else {
          // Formato antiguo: objeto con claves directas - convertir a claves únicas
          Object.keys(dataResult).forEach((key) => {
            // Para compatibilidad con datos antiguos, asignar a todos los grupos que tengan esa clave
            // Nota: esto es un fallback, idealmente todos los datos nuevos usan el formato array
            parsedData.forEach((templateItem) => {
              if (templateItem.KeyValue === key) {
                const uniqueKey = `${templateItem.Group}|${key}`;
                formDataForForm[uniqueKey] = dataResult[key];
              }
            });
          });
        }

        frmRef.current?.instance.updateData(formDataForForm);
        setIsChanged(false);
      }
    }
  }, [dataModal]);

  const handleFocusIn = useCallback((e: FocusInEvent) => {
    e.component.open();
  }, []);

  const handleClose = useCallback((update: boolean) => {
    frmRef.current?.instance.resetValues();
    dispatch(showModalTemplates({ Visible: false, An_Id: -1, Res_Id: -1, An_Nombre: '', Res_Estado: ResultEstadoResult.Validado, Res_Resultado: '', Update: update, Pc_Id: -1 }));
  }, []);

  const handleCancel = useCallback(() => {
    handleClose(false);
  }, [handleClose]);

  const handleSaveTemplate = useCallback(() => {
    setModalPlantillaVisible(true);
  }, [setModalPlantillaVisible]);

  // Función para ordenar los parámetros según la plantilla base
  const createOrderedResult = useCallback(() => {
    const groupMap = new Map<string, { Key: string; Label: string; Value: unknown; Type: string }[]>();

    // Procesar en el orden de la plantilla base
    data.forEach((templateItem) => {
      const clave = templateItem.KeyValue;
      const uniqueKey = `${templateItem.Group}|${clave}`;
      let valor = dataFrm[uniqueKey];

      // Fallback para manejar casos donde DevExtreme cambia '.' por ''
      if (valor === undefined && templateItem.KeyValue === '.') {
        const fallbackKey = `${templateItem.Group}|`;
        valor = dataFrm[fallbackKey];
      }

      // Solo procesar si hay un valor (no null ni undefined)
      if (valor !== undefined && valor !== null) {
        // Normalizar valor: extraer valor interno si es un objeto con una sola propiedad
        let normalizedValue: unknown = valor;
        if (typeof valor === 'object' && !Array.isArray(valor) && valor !== null) {
          const innerKeys = Object.keys(valor as object);
          if (innerKeys.length === 1) {
            normalizedValue = (valor as any)[innerKeys[0]];
          }
        }

        const newItem = {
          Key: templateItem.KeyValue,
          Label: templateItem.Label,
          Value: normalizedValue,
          Type: templateItem.Type,
        };

        const group = templateItem.Group;
        if (!groupMap.has(group)) {
          groupMap.set(group, []);
        }
        groupMap.get(group)!.push(newItem);
      }
    });

    // Convertir el mapa a array manteniendo el orden de los grupos según aparecen en la plantilla
    const orderedGroups: string[] = [];
    data.forEach((item) => {
      if (!orderedGroups.includes(item.Group) && groupMap.has(item.Group)) {
        orderedGroups.push(item.Group);
      }
    });

    return orderedGroups.map((group) => ({
      Group: group,
      Items: groupMap.get(group) || [],
    }));
  }, [data, dataFrm]);

  const handleConfirm = useCallback(async () => {
    if (loading) {
      return;
    }
    if (!isChanged) {
      newToast('INFO', 'No se detectaron cambios', 4);
      return;
    }

    // Usar la función de ordenamiento en lugar del reduce anterior
    const nuevoArray = createOrderedResult();

    //Este es el objeto a guardar en base de datos - ambos campos tendrán exactamente la misma información
    setLoading(true);
    const resultString = nuevoArray.length > 0 ? JSON.stringify(nuevoArray) : '';

    const resp = await putResultTemplate({
      keys: [dataModal?.Res_Id],
      Result: resultString,
      ResultTemp: resultString,
    });
    setLoading(false);
    if (resp) {
      handleClose(true);
    }
  }, [isChanged, dataModal, createOrderedResult]);

  const handleCloseTemplate = useCallback(() => {
    setModalPlantillaVisible(false);
    frmRefTemplate.current?.instance.resetValues();
    setGroups({}); // Reset groups when the modal is closed
  }, [frmRefTemplate, setGroups]);

  const handleConfirmTemplate = useCallback(() => {
    if (!frmRefTemplate.current?.instance.validate().isValid) {
      return;
    }
    if (Object.keys(dataFrm).length === 0 || Object.values(dataFrm).every((x) => x === '' || x === null)) {
      newToast('WARNING', 'Debe añadir primero algunas respuestas para esta plantilla', 5);
      handleCloseTemplate();
      return;
    }
  }, [frmRefTemplate]);

  const getIconStatus = () => {
    switch (dataModal?.Res_Estado) {
      case ResultEstadoResult.SinResultado:
      default:
        return resGridStatus1;
      case ResultEstadoResult.ConResultado:
        return resGridStatus2;
      case ResultEstadoResult.Validado:
        return resGridStatus3;
      case ResultEstadoResult.Impreso:
        return resGridStatus4;
      case ResultEstadoResult.OtrasFuentes:
        return resGridStatus5;
      case ResultEstadoResult.ValidadoBlanco:
        return resButtonsPin;
    }
  };

  const getTextStatus = () => {
    switch (dataModal?.Res_Estado) {
      case ResultEstadoResult.SinResultado:
      default:
        return 'SIN RESULTADO';
      case ResultEstadoResult.ConResultado:
        return 'CON RESULTADO';
      case ResultEstadoResult.Validado:
        return 'VALIDADO';
      case ResultEstadoResult.Impreso:
        return 'IMPRESO';
      case ResultEstadoResult.OtrasFuentes:
        return 'OTRAS FUENTES';
      case ResultEstadoResult.ValidadoBlanco:
        return 'FIJADO EN BLANCO';
    }
  };

  const handleDataChange = useCallback(() => {
    setIsChanged(true);
  }, [setIsChanged]);

  return (
    <Popup visible={dataModal?.Visible || false} onHidden={handleCancel} fullScreen={isMedium || isSmall || isXSmall} width={600} height={'95%'} title={dataModal?.An_Nombre || ''}>
      <ScrollView>
        <Toolbar>
          <Item widget='dxButton' locateInMenu='auto' location='before'>
            <Button icon={getIconStatus()} text={getTextStatus()} stylingMode='text' />
          </Item>
          <Item location='before' locateInMenu='auto'>
            <div className='separator' />
          </Item>
          <Item widget='dxSelectBox' locateInMenu='auto' location='before'>
            <SelectBox
              disabled={(dataModal && dataModal?.Res_Estado >= 3) || false}
              width={'100%'}
              label='Plantillas predeterminadas'
              onValueChanged={(e) => {
                setSaveTemplate({ Nombre: e.value });
                setModalPlantillaVisible(true);
              }}
              labelMode='static'>
              {Prm_Conf_Campos_Diseno && <SelectBoxButton name='Edit' location='after' options={{ icon: 'edit' }} />}
            </SelectBox>
          </Item>
          <Item location='before' locateInMenu='auto'>
            <div className='separator' />
          </Item>
          <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button
              disabled={(dataModal && dataModal?.Res_Estado >= 3) || false}
              id='ClarearResultt'
              icon={resButtonsClear}
              text='Clarear resultado'
              onClick={() => {
                frmRef.current?.instance.resetValues();
              }}
              stylingMode='text'
            />
            <Tooltip target={'#ClarearResultt'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Limpiar todos los resultados
            </Tooltip>
          </Item>
        </Toolbar>

        <Form id='ppal' formData={dataFrm} onFieldDataChanged={handleDataChange} readOnly={(dataModal && dataModal?.Res_Estado >= 3) || false} labelLocation='left' className='resultTemplates' ref={frmRef}>
          {Object.entries(groups).map(([groupName, groupItems]) => (
            <GroupItem key={groupName} caption={groupName} colCount={2}>
              {groupItems.map((item: FormData, index) => {
                // Crear una clave única que incluya el grupo para evitar conflictos
                const uniqueKey = `${groupName}|${item.KeyValue}`;

                switch (item.Type) {
                  case 'dxSelectBox':
                    return (
                      <SimpleItem key={`prm_${index}`} editorType={item.Type} editorOptions={{ onFocusIn: handleFocusIn, items: item.Items, acceptCustomValue: true, searchEnabled: true, searchTimeout: 0 }} dataField={uniqueKey} colSpan={item.ColSpan}>
                        <Label text={item.Label} />
                      </SimpleItem>
                    );
                  case 'dxTagBox':
                    return (
                      <SimpleItem key={`prm_${index}`} editorType={item.Type} editorOptions={{ onFocusIn: handleFocusIn, items: item.Items, acceptCustomValue: true, searchEnabled: true, searchTimeout: 0 }} dataField={uniqueKey} colSpan={item.ColSpan}>
                        <Label text={item.Label} />
                      </SimpleItem>
                    );
                  case 'dxTextArea':
                    return (
                      <SimpleItem key={`prm_${index}`} dataField={uniqueKey} editorType='dxTextArea' editorOptions={{ height: 70 }} colSpan={item.ColSpan}>
                        <Label text={item.Label} />
                      </SimpleItem>
                    );
                  default:
                    return (
                      <SimpleItem key={`prm_${index}`} dataField={uniqueKey} colSpan={item.ColSpan}>
                        <Label text={item.Label} />
                      </SimpleItem>
                    );
                }
              })}
            </GroupItem>
          ))}
        </Form>
      </ScrollView>
      {Prm_Conf_Campos_Diseno && dataModal && dataModal?.Res_Estado < 3 && <ToolbarItem widget='dxButton' toolbar='top' locateInMenu='always' options={{ text: 'GUARDAR COMO PLANTILLA', type: 'normal', onClick: handleSaveTemplate }} />}
      <ToolbarItem widget='dxButton' toolbar='bottom' location='after' options={{ text: 'CANCELAR', type: 'normal', onClick: handleCancel }} />
      {dataModal && dataModal?.Res_Estado < 3 && <ToolbarItem widget='dxButton' toolbar='bottom' location='after' options={{ text: loading ? 'PROCESANDO...' : 'CONFIRMAR', stylingMode: 'contained', type: 'default', onClick: handleConfirm }} />}
      <Popup title='Plantilla predeterminada' showCloseButton={true} visible={modalPlantillaVisible} onHidden={handleCloseTemplate} width={300} height={'auto'} fullScreen={isXSmall}>
        <Form formData={saveTemplate} ref={frmRefTemplate}>
          <SimpleItem isRequired dataField='Nombre'>
            <RequiredRule message='Debe indicar el nombre de la plantilla' />
            <Label text='Indique el nombre de la plantilla' />
          </SimpleItem>
        </Form>
        <ToolbarItem widget='dxButton' toolbar='bottom' location='after' options={{ text: 'GUARDAR', stylingMode: 'contained', type: 'default', onClick: handleConfirmTemplate }} />
      </Popup>
    </Popup>
  );
};
